import {
  AudioOutlined,
  CaretRightOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  LoadingOutlined,
  PauseOutlined,
  RightOutlined
} from '@ant-design/icons';
import { Alert, Button, Input, Modal, Spin } from 'antd';
import { useGetRecordingLimitsQuery } from 'api/CommonApi';
import {
  useGetMyLifeReviewAudioUploadUrlMutation,
  useGetMyLifeReviewResponseAudioDownloadUrlMutation,
  useTranscribeMyLifeReviewResponseMutation,
  useUpdateMyLifeReviewResponseMutation
} from 'api/LifeReviewApi';
import { UserAccessLevelPlanEnum } from 'api/UsersApi';
import { useReactMediaRecorder } from 'components/Tool/ReactMediaRecorder/ReactMediaRecorder';
import ContainerComponent from 'components/Tool/ContainerComponent/ContainerComponent';
import Footer from 'pages/Client/Footer/Footer';
import Header from 'pages/Client/Header/Header';
import { useLifeReviewMediaState } from 'pages/LifeReview/useLifeReviewMediaState';
import { getPromptDisplayText, useLifeReviewSessionState } from 'pages/LifeReview/useLifeReviewSessionState';
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LuPencilLine, LuRotateCcw } from 'react-icons/lu';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { selectAuthUser } from 'store/authSlice';
import { sessionActivity } from 'utils/sessionActivity';
import { userHasAccess } from 'utils/utils';
import './LifeReviewSessionPage.css';

const DEFAULT_WARNING_DURATION_SECONDS = 15 * 60;
const DEFAULT_MAX_DURATION_SECONDS = 20 * 60;
const TRANSCRIPTION_FAILURE_MESSAGE = 'Recording saved — transcription could not be completed.';
const MIN_VALID_AUDIO_UPLOAD_BYTES = 1024;

type GuardrailSource = 'duration' | 'size';

const formatSizeThreshold = (sizeBytes: number) => {
  if (sizeBytes >= 1024 * 1024) {
    return `${Math.round((sizeBytes / (1024 * 1024)) * 10) / 10} MB`;
  }

  if (sizeBytes >= 1024) {
    return `${Math.round((sizeBytes / 1024) * 10) / 10} KB`;
  }

  return `${sizeBytes} bytes`;
};

const formatDurationThreshold = (durationSeconds: number) => {
  const minutes = Math.floor(durationSeconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

const isIpadSafari = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent || '';
  const isSafari = /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/i.test(userAgent);
  const isIpadLike = /iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && window.navigator.maxTouchPoints > 1);

  return isSafari && isIpadLike;
};

export function LifeReviewSessionPage() {
  const user = useSelector(selectAuthUser);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    sections,
    isLoading,
    isSaving,
    saveError,
    selectedPrompt,
    selectedPromptId,
    selectedSectionIndex,
    draftByPromptId,
    persistedByPromptId,
    persistedCompletedByPromptId,
    persistedResponseByPromptId,
    currentPromptValue,
    setCurrentPromptValue,
    goToPrompt,
    saveCurrentPrompt,
    savePromptAudio,
    refreshLifeReview
  } = useLifeReviewSessionState(searchParams.get('promptId') || undefined);

  const { uploadStatus, transcriptStatus, setUploadStatus, setTranscriptStatus, setPlaybackStatus } = useLifeReviewMediaState();
  const [getAudioUploadUrl] = useGetMyLifeReviewAudioUploadUrlMutation();
  const [getAudioDownloadUrl] = useGetMyLifeReviewResponseAudioDownloadUrlMutation();
  const { data: recordingLimitsConfig } = useGetRecordingLimitsQuery();
  const [transcribeResponse, { isLoading: isRetryingTranscript }] = useTranscribeMyLifeReviewResponseMutation();
  const [updateResponse, { isLoading: isSavingTranscriptEdit }] = useUpdateMyLifeReviewResponseMutation();
  const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | null>(null);
  const [recordedPromptId, setRecordedPromptId] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState('');
  const [transcriptError, setTranscriptError] = useState('');
  const [transcriptSaveSuccess, setTranscriptSaveSuccess] = useState('');
  const [playbackUrl, setPlaybackUrl] = useState('');
  const [transcriptDraft, setTranscriptDraft] = useState('');
  const [activeTab, setActiveTab] = useState<'record' | 'write' | 'transcript'>('record');
  const [recordingElapsedSeconds, setRecordingElapsedSeconds] = useState(0);
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [isReplacingRecording, setIsReplacingRecording] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [completeSectionModalOpen, setCompleteSectionModalOpen] = useState(false);
  const hasLifeReviewPaidAccess = Boolean(userHasAccess(user, UserAccessLevelPlanEnum.Premium));
  const [isSaveAndNextConfirmationStep, setIsSaveAndNextConfirmationStep] = useState(false);
  const [isSaveAndNextPending, setIsSaveAndNextPending] = useState(false);
  const [isSaveAndClosePending, setIsSaveAndClosePending] = useState(false);
  const [saveStatusLabel, setSaveStatusLabel] = useState('');
  const [saveToastMessage, setSaveToastMessage] = useState('');
  const [isTranscriptPreviewExpanded, setIsTranscriptPreviewExpanded] = useState(false);
  const [hasTranscriptPreviewOverflow, setHasTranscriptPreviewOverflow] = useState(false);
  const [postStopAction, setPostStopAction] = useState<'none' | 'saveAndNext' | 'saveAndClose' | 'skip'>('none');

  const selectedPromptPersistedResponse = selectedPromptId ? persistedResponseByPromptId[selectedPromptId] : undefined;
  const selectedPromptResponseId = selectedPromptPersistedResponse?.id || '';
  const selectedPromptHydratedAudioUrl = selectedPromptPersistedResponse?.audioUrl || selectedPromptPersistedResponse?.audioDownloadUrl || '';
  const hasSavedAudioForSelectedPrompt = Boolean(
    selectedPromptPersistedResponse?.hasAudio || selectedPromptHydratedAudioUrl || selectedPromptPersistedResponse?.audioStorageKey
  );
  const selectedPromptTranscriptText = (selectedPromptPersistedResponse?.transcriptText || '').trim();
  const selectedPromptTranscriptEditorInitialValue = selectedPromptPersistedResponse?.editedTranscriptText ?? selectedPromptPersistedResponse?.transcriptText ?? '';
  const selectedPromptPersistedValue = selectedPrompt ? persistedByPromptId[selectedPrompt.id] || '' : '';
  const hasWriteChanges = currentPromptValue.trim() !== selectedPromptPersistedValue.trim();

  const saveFeedbackTimeoutRef = useRef<number | null>(null);
  const saveAndNextConfirmTimeoutRef = useRef<number | null>(null);
  const transcriptPreviewTextRef = useRef<HTMLSpanElement | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingPromptIdRef = useRef<string | null>(null);
  const discardPendingOnStopRef = useRef(false);
  const recordingBytesRef = useRef(0);
  const warningShownRef = useRef(false);
  const autoStopTriggeredRef = useRef(false);
  const guardrailSourceRef = useRef<GuardrailSource | null>(null);
  const saveAndNextPrimaryButtonRef = useRef<HTMLButtonElement | null>(null);
  const saveAndNextSecondaryButtonRef = useRef<HTMLButtonElement | null>(null);

  const effectiveWarningDurationSeconds = recordingLimitsConfig?.warningDurationSeconds || DEFAULT_WARNING_DURATION_SECONDS;
  const effectiveMaxDurationSeconds = recordingLimitsConfig?.maxDurationSeconds || DEFAULT_MAX_DURATION_SECONDS;
  const warningSizeBytes = recordingLimitsConfig?.warningSizeBytes || null;
  const maxSizeBytes = recordingLimitsConfig?.maxSizeBytes || null;
  const isSizeGuardrailEnabled = Boolean(warningSizeBytes || maxSizeBytes);

  const resetRecordingGuardrails = useCallback(() => {
    recordingBytesRef.current = 0;
    warningShownRef.current = false;
    autoStopTriggeredRef.current = false;
    guardrailSourceRef.current = null;
  }, []);

  useEffect(() => {
    setTranscriptDraft(selectedPromptTranscriptEditorInitialValue);
  }, [selectedPromptId, selectedPromptTranscriptEditorInitialValue]);

  useEffect(() => {
    setSaveStatusLabel('');
    setSaveToastMessage('');
    setIsReplacingRecording(false);
    setIsTranscriptPreviewExpanded(false);
    if (saveFeedbackTimeoutRef.current) {
      window.clearTimeout(saveFeedbackTimeoutRef.current);
      saveFeedbackTimeoutRef.current = null;
    }
    resetRecordingGuardrails();
  }, [resetRecordingGuardrails, selectedPromptId]);

  useEffect(() => {
    return () => {
      if (saveFeedbackTimeoutRef.current) {
        window.clearTimeout(saveFeedbackTimeoutRef.current);
      }
      if (saveAndNextConfirmTimeoutRef.current) {
        window.clearTimeout(saveAndNextConfirmTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isSaveAndNextConfirmationStep) {
      return;
    }

    saveAndNextPrimaryButtonRef.current?.focus();
  }, [isSaveAndNextConfirmationStep]);

  const supportedAudioMimeType = useMemo(() => {
    const isIpadSafariBrowser = isIpadSafari();
    const candidateMimeTypes = isIpadSafariBrowser
      ? ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
      : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/wav'];
    const mediaRecorderConstructor = window.MediaRecorder;

    if (!mediaRecorderConstructor?.isTypeSupported) {
      if (isIpadSafariBrowser) {
        console.info('[LifeReviewAudio][iPad] MediaRecorder.isTypeSupported unavailable');
      }
      return undefined;
    }

    const selectedMimeType = candidateMimeTypes.find((mimeType) => mediaRecorderConstructor.isTypeSupported(mimeType));

    if (isIpadSafariBrowser) {
      console.info('[LifeReviewAudio][iPad] selected recorder MIME type', {
        selectedMimeType,
        candidateMimeTypes
      });
    }

    return selectedMimeType;
  }, []);

  const clearBlobUrlRef = useRef<() => void>(() => undefined);
  const onStopRef = useRef<(blobUrl: string, blob: Blob) => void>(() => undefined);
  const stopRecordingRef = useRef<() => void>(() => undefined);

  const evaluateGuardrails = useCallback(
    (source: GuardrailSource) => {
      if (autoStopTriggeredRef.current) {
        return;
      }

      const elapsedSeconds = recordingElapsedSeconds;
      const bytesCaptured = recordingBytesRef.current;
      const reachedWarningDuration = elapsedSeconds >= effectiveWarningDurationSeconds;
      const reachedMaxDuration = elapsedSeconds >= effectiveMaxDurationSeconds;
      const reachedWarningSize = Boolean(warningSizeBytes && bytesCaptured > 0 && bytesCaptured >= warningSizeBytes);
      const reachedMaxSize = Boolean(maxSizeBytes && bytesCaptured > 0 && bytesCaptured >= maxSizeBytes);

      if (reachedMaxDuration || reachedMaxSize) {
        autoStopTriggeredRef.current = true;
        setMediaError(
          reachedMaxDuration
            ? 'Maximum recording length reached. Recording stopped automatically.'
            : 'Maximum recording size reached. Recording stopped automatically.'
        );
        stopRecordingRef.current();
        return;
      }

      if (!guardrailSourceRef.current) {
        if (source === 'duration' && (reachedWarningDuration || reachedMaxDuration)) {
          guardrailSourceRef.current = 'duration';
        }

        if (!guardrailSourceRef.current && source === 'size' && (reachedWarningSize || reachedMaxSize)) {
          guardrailSourceRef.current = 'size';
        }
      }

      const activeSource = guardrailSourceRef.current;
      if (!activeSource) {
        return;
      }

      const reachedWarning = activeSource === 'duration' ? reachedWarningDuration : reachedWarningSize;

      if (reachedWarning && !warningShownRef.current) {
        warningShownRef.current = true;
        if (activeSource === 'duration') {
          setMediaError(
            `You have reached ${formatDurationThreshold(effectiveWarningDurationSeconds)}. Recording will stop at ${formatDurationThreshold(
              effectiveMaxDurationSeconds
            )}.`
          );
        } else if (warningSizeBytes) {
          setMediaError(`Recording is approaching the size limit (${formatSizeThreshold(warningSizeBytes)}).`);
        }
      }
    },
    [
      effectiveMaxDurationSeconds,
      effectiveWarningDurationSeconds,
      maxSizeBytes,
      recordingElapsedSeconds,
      warningSizeBytes
    ]
  );

  const { status: recorderStatus, error: recorderError, mediaBlobUrl, startRecording, pauseRecording, resumeRecording, stopRecording, clearBlobUrl } = useReactMediaRecorder({
    video: false,
    audio: true,
    askPermissionOnMount: false,
    preferRecordedMimeType: isIpadSafari(),
    mediaRecorderOptions: supportedAudioMimeType ? { mimeType: supportedAudioMimeType } : undefined,
    onChunkCaptured: (chunk, accumulatedSizeBytes) => {
      if (!isSizeGuardrailEnabled) {
        return;
      }

      const size = typeof chunk?.size === 'number' && Number.isFinite(chunk.size) ? chunk.size : 0;
      if (size <= 0) {
        return;
      }

      recordingBytesRef.current = accumulatedSizeBytes;
      evaluateGuardrails('size');
    },
    onStop: (blobUrl: string, blob: Blob) => {
      onStopRef.current(blobUrl, blob);
    }
  });

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  useEffect(() => {
    clearBlobUrlRef.current = clearBlobUrl;
  }, [clearBlobUrl]);

  useEffect(() => {
    onStopRef.current = (blobUrl: string, blob: Blob) => {
      if (discardPendingOnStopRef.current) {
        discardPendingOnStopRef.current = false;
        recordingPromptIdRef.current = null;
        setRecordedAudioBlob(null);
        setRecordedPromptId(null);
        setUploadStatus('idle');
        resetRecordingGuardrails();
        clearBlobUrlRef.current();
        return;
      }

      setRecordedAudioBlob(blob);
      setRecordedPromptId(recordingPromptIdRef.current ?? selectedPromptId);
      setUploadStatus('idle');

      if (isIpadSafari()) {
        console.info('[LifeReviewAudio][iPad] onStop finalized blob', {
          selectedMimeType: supportedAudioMimeType,
          finalBlobType: blob.type,
          finalBlobSize: blob.size,
          localPlaybackUrlReady: Boolean(blobUrl)
        });
      }
    };
  }, [resetRecordingGuardrails, selectedPromptId, setUploadStatus, supportedAudioMimeType]);

  useEffect(() => {
    if (recorderError) {
      console.error('[LifeReviewAudio] recorder error', {
        selectedPromptId,
        recorderError
      });
    }
  }, [recorderError, selectedPromptId]);

  useEffect(() => {
    setPlaybackUrl('');
    setMediaError('');
    setPlaybackStatus('idle');
  }, [selectedPromptId, setPlaybackStatus]);

  useEffect(() => {
    const rawTranscriptionStatus = (selectedPromptPersistedResponse?.transcriptionStatus || '').toLowerCase();

    if (selectedPromptTranscriptText) {
      setTranscriptStatus('completed');
      setTranscriptError('');
      return;
    }

    if (rawTranscriptionStatus.includes('error') || rawTranscriptionStatus.includes('failed')) {
      setTranscriptStatus('failed');
      setTranscriptError(selectedPromptPersistedResponse?.transcriptionError || TRANSCRIPTION_FAILURE_MESSAGE);
      return;
    }

    if (hasSavedAudioForSelectedPrompt) {
      setTranscriptStatus('pending');
      setTranscriptError('');
      return;
    }

    setTranscriptStatus('idle');
    setTranscriptError('');
  }, [
    hasSavedAudioForSelectedPrompt,
    selectedPromptPersistedResponse?.transcriptionError,
    selectedPromptPersistedResponse?.transcriptionStatus,
    selectedPromptTranscriptText,
    setTranscriptStatus
  ]);

  useEffect(() => {
    const rawTranscriptionStatus = (selectedPromptPersistedResponse?.transcriptionStatus || '').toLowerCase();
    const isTranscriptionPending =
      hasSavedAudioForSelectedPrompt &&
      !selectedPromptTranscriptText &&
      !(rawTranscriptionStatus.includes('error') || rawTranscriptionStatus.includes('failed'));

    if (!selectedPromptResponseId || !isTranscriptionPending) {
      return;
    }

    const initialDelayMs = 5000;
    const maxDelayMs = 15000;
    const maxPollingWindowMs = 2 * 60 * 1000;
    const startedAt = Date.now();
    let attemptCount = 0;
    let timeoutId: number | undefined;
    let cancelled = false;
    let isRefreshing = false;

    const scheduleRefresh = () => {
      if (cancelled) {
        return;
      }

      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs >= maxPollingWindowMs) {
        setTranscriptStatus('failed');
        setTranscriptError('Transcription is taking longer than expected. Please retry.');
        return;
      }

      const nextDelayMs = Math.min(initialDelayMs * Math.pow(2, attemptCount), maxDelayMs);
      timeoutId = window.setTimeout(async () => {
        if (cancelled || isRefreshing) {
          return;
        }

        isRefreshing = true;
        try {
          await refreshLifeReview();
        } finally {
          isRefreshing = false;
        }

        attemptCount += 1;
        scheduleRefresh();
      }, nextDelayMs);
    };

    scheduleRefresh();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    hasSavedAudioForSelectedPrompt,
    refreshLifeReview,
    selectedPromptPersistedResponse?.transcriptionStatus,
    selectedPromptResponseId,
    selectedPromptTranscriptText,
    setTranscriptError,
    setTranscriptStatus
  ]);

  useEffect(() => {
    return () => {
      clearBlobUrlRef.current();
    };
  }, []);

  const handleStartRecording = async () => {
    setMediaError('');
    resetRecordingGuardrails();
    recordingPromptIdRef.current = selectedPromptId;

    try {
      const permissionApi = navigator.permissions;
      if (permissionApi?.query) {
        try {
          const permissionStatus = await permissionApi.query({ name: 'microphone' as PermissionName });
          console.info('[LifeReviewAudio] mic permission status', { state: permissionStatus.state });
        } catch (permissionError) {
          console.info('[LifeReviewAudio] mic permission query unavailable', permissionError);
        }
      }

      const mediaDevices = navigator.mediaDevices;
      if (!mediaDevices?.getUserMedia) {
        setMediaError('Microphone recording is not supported in this browser.');
        return;
      }

      if (mediaDevices.enumerateDevices) {
        const devices = await mediaDevices.enumerateDevices();
        const hasAudioInput = devices.some((device) => device.kind === 'audioinput');
        console.info('[LifeReviewAudio] audio input devices', {
          totalDevices: devices.length,
          audioInputCount: devices.filter((device) => device.kind === 'audioinput').length
        });

        if (!hasAudioInput) {
          setMediaError('No microphone device is available.');
          return;
        }
      }

      startRecording();
    } catch (error: any) {
      console.error('[LifeReviewAudio] failed to start recording', error);
      setMediaError(error?.message || 'Unable to start recording. Please check microphone permissions and try again.');
    }
  };

  const clearLocalRecording = useCallback(() => {
    discardPendingOnStopRef.current = false;
    recordingPromptIdRef.current = null;
    setPostStopAction('none');
    setRecordedAudioBlob(null);
    setRecordedPromptId(null);
    setUploadStatus('idle');
    resetRecordingGuardrails();
    clearBlobUrlRef.current();
  }, [resetRecordingGuardrails, setUploadStatus]);

  const handleDiscardRecording = () => {
    const hasLocalRecordingForPrompt = Boolean(recordedAudioBlob && recordedPromptId === selectedPromptId);
    const isRecordingInProgress = recorderStatus === 'recording' || recorderStatus === 'paused';

    if (!hasLocalRecordingForPrompt && !isRecordingInProgress) {
      return;
    }

    Modal.confirm({
      title: 'Discard this recording?',
      content: 'This cannot be undone.',
      okText: 'Discard',
      okType: 'danger',
      cancelText: 'Keep recording',
      onOk: () => {
        setPostStopAction('none');
        if (isRecordingInProgress) {
          discardPendingOnStopRef.current = true;
          stopRecording();
          return;
        }

        clearLocalRecording();
      }
    });
  };

  const handleStartOverRecording = () => {
    const hasLocalRecordingForPrompt = Boolean(recordedAudioBlob && recordedPromptId === selectedPromptId);
    const isRecordingInProgress = recorderStatus === 'recording' || recorderStatus === 'paused';

    if (!hasLocalRecordingForPrompt && !isRecordingInProgress) {
      return;
    }

    Modal.confirm({
      title: 'Start over?',
      content: 'Your current recording will be lost.',
      okText: 'Start over',
      okType: 'danger',
      cancelText: 'Keep recording',
      onOk: () => {
        setPostStopAction('none');
        if (isRecordingInProgress) {
          discardPendingOnStopRef.current = true;
          stopRecording();
          return;
        }

        clearLocalRecording();
      }
    });
  };

  const handleUploadAndSaveAudio = useCallback(async () => {
    if (!selectedPrompt?.id || !recordedAudioBlob) {
      return false;
    }

    if (recordedPromptId !== selectedPrompt.id) {
      setMediaError('This recording belongs to a different prompt. Please re-record for this prompt before uploading.');
      return false;
    }

    try {
      setMediaError('');
      setUploadStatus('preparing');

      if (recordedAudioBlob.size < MIN_VALID_AUDIO_UPLOAD_BYTES) {
        setUploadStatus('error');
        setMediaError('Recording failed because the audio file was empty or incomplete. Please try again.');
        return false;
      }

      const rawContentType = recordedAudioBlob.type || supportedAudioMimeType || '';
      const contentType = rawContentType.split(';')[0] || '';

      if (!contentType) {
        setUploadStatus('error');
        setMediaError('Recording format could not be verified. Please try recording again.');
        return false;
      }

      if (isIpadSafari()) {
        const normalizedBlobType = (recordedAudioBlob.type || '').split(';')[0] || '';
        console.info('[LifeReviewAudio][iPad] pre-upload diagnostics', {
          selectedMimeType: supportedAudioMimeType,
          blobType: recordedAudioBlob.type,
          blobSize: recordedAudioBlob.size,
          rawContentType,
          uploadContentType: contentType,
          usedSelectedMimeTypeFallback: !normalizedBlobType && Boolean(supportedAudioMimeType),
          contentTypeMatchesBlobType: normalizedBlobType === contentType
        });

        if (normalizedBlobType && normalizedBlobType !== contentType) {
          console.warn('[LifeReviewAudio][iPad] upload content-type mismatch', {
            blobType: recordedAudioBlob.type,
            uploadContentType: contentType
          });
        }
      }

      const { uploadUrl, audioStorageKey } = await getAudioUploadUrl({
        promptTemplateId: selectedPrompt.id,
        contentType
      }).unwrap();

      if (!uploadUrl) {
        setUploadStatus('error');
        setMediaError('Unable to get audio upload URL. Please refresh and try again.');
        return false;
      }

      setUploadStatus('uploading');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType
        },
        body: recordedAudioBlob
      });

      if (!uploadResponse.ok) {
        setUploadStatus('error');
        setMediaError('Audio upload failed');
        return false;
      }

      const didSavePromptAudio = await savePromptAudio(selectedPrompt.id, audioStorageKey);
      if (!didSavePromptAudio) {
        setUploadStatus('error');
        setMediaError('Audio uploaded, but saving it to your response failed. Please try again.');
        return false;
      }

      setUploadStatus('success');
      clearLocalRecording();
      return true;
    } catch (error: any) {
      setUploadStatus('error');
      setMediaError(error?.data?.message || error?.message || 'Unable to upload audio right now. Please try again.');
      return false;
    }
  }, [clearLocalRecording, getAudioUploadUrl, recordedAudioBlob, recordedPromptId, savePromptAudio, selectedPrompt?.id, setUploadStatus, supportedAudioMimeType]);

  const hasLocalRecordingForSelectedPrompt = Boolean(recordedAudioBlob && mediaBlobUrl && recordedPromptId === selectedPromptId);
  const showSavedRecordingCard = hasSavedAudioForSelectedPrompt && !hasLocalRecordingForSelectedPrompt && !isReplacingRecording;
  const isRecordingActive = recorderStatus === 'recording';
  const isRecordingPaused = recorderStatus === 'paused';
  const isRecordingInProgress = isRecordingActive || isRecordingPaused;
  const isUploadingRecording = uploadStatus === 'preparing' || uploadStatus === 'uploading';
  const hasStoppedRecordingForSelectedPrompt = hasLocalRecordingForSelectedPrompt && recorderStatus === 'stopped';
  const hasRecordChanges = hasLocalRecordingForSelectedPrompt;
  const canSubmitRecordSave = hasRecordChanges || isRecordingPaused;
  const hasTranscriptTab = Boolean(selectedPrompt);

  useEffect(() => {
    sessionActivity.setRecordingActive(isRecordingInProgress);

    return () => {
      sessionActivity.setRecordingActive(false);
    };
  }, [isRecordingInProgress]);

  useEffect(() => {
    sessionActivity.setUploadActive(isUploadingRecording);

    return () => {
      sessionActivity.setUploadActive(false);
    };
  }, [isUploadingRecording]);

  const handleRetryTranscription = async () => {
    if (!selectedPromptResponseId) {
      return;
    }

    try {
      setTranscriptError('');
      setTranscriptStatus('pending');
      await transcribeResponse({ responseId: selectedPromptResponseId }).unwrap();
      await refreshLifeReview().unwrap();
    } catch (error: any) {
      setTranscriptStatus('failed');
      setTranscriptError(error?.data?.message || error?.message || TRANSCRIPTION_FAILURE_MESSAGE);
    }
  };

  const handleSaveTranscriptEdit = async () => {
    if (!selectedPromptResponseId) {
      return;
    }

    try {
      setTranscriptError('');
      setTranscriptSaveSuccess('');
      await updateResponse({
        responseId: selectedPromptResponseId,
        editedTranscriptText: transcriptDraft
      }).unwrap();
      await refreshLifeReview().unwrap();
      setTranscriptSaveSuccess('Transcript saved');
    } catch (error: any) {
      setTranscriptError(error?.data?.message || error?.message || 'Unable to save transcript edits right now.');
    }
  };

  const stopAnswerModalAudioPlayback = useCallback(() => {
    const answerModal = document.querySelector('.life-review-session-answer-modal');
    if (!(answerModal instanceof HTMLElement)) {
      return;
    }

    const audioElements = answerModal.querySelectorAll('audio');
    audioElements.forEach((audioElement) => {
      if (!(audioElement instanceof HTMLAudioElement)) {
        return;
      }

      audioElement.pause();
      audioElement.currentTime = 0;
    });
  }, []);

  const showSavedFeedback = useCallback(() => {
    setSaveStatusLabel('Saved');
    setSaveToastMessage('Your answer has been saved.');
    if (saveFeedbackTimeoutRef.current) {
      window.clearTimeout(saveFeedbackTimeoutRef.current);
    }
    saveFeedbackTimeoutRef.current = window.setTimeout(() => {
      setSaveStatusLabel('');
      setSaveToastMessage('');
    }, 2500);
  }, []);

  const handleWriteSaveAndNext = async () => {
    setIsSaveAndNextPending(true);

    if (hasRecordChanges || isRecordingInProgress) {
      if (isRecordingInProgress) {
        setPostStopAction('saveAndNext');
        stopRecording();
        return;
      }

      await handleRecordSaveAndNext();
      return;
    }

    const didSave = await Promise.resolve(saveCurrentPrompt());
    if (didSave) {
      showSavedFeedback();
      setIsSaveAndNextConfirmationStep(true);
    }

    setIsSaveAndNextPending(false);
  };

  const handleRecordSaveAndNext = useCallback(async () => {
    const didSave = await handleUploadAndSaveAudio();
    if (!didSave) {
      setIsSaveAndNextPending(false);
      return;
    }

    if (hasWriteChanges) {
      const didSaveWrite = await Promise.resolve(saveCurrentPrompt());
      if (!didSaveWrite) {
        setIsSaveAndNextPending(false);
        return;
      }
    }

    showSavedFeedback();
    setIsReplacingRecording(false);
    setIsSaveAndNextConfirmationStep(true);
    setIsSaveAndNextPending(false);
  }, [handleUploadAndSaveAudio, hasWriteChanges, saveCurrentPrompt, showSavedFeedback]);

  const handleRecordSaveAndNextRequest = () => {
    setIsSaveAndNextPending(true);
    if (isRecordingPaused) {
      setPostStopAction('saveAndNext');
      stopRecording();
      return;
    }

    void handleRecordSaveAndNext();
  };

  const handleWriteSaveAndClose = async () => {
    setIsSaveAndClosePending(true);

    if (hasRecordChanges || isRecordingInProgress) {
      if (isRecordingInProgress) {
        setPostStopAction('saveAndClose');
        stopRecording();
        return;
      }

      await handleRecordSaveAndClose();
      return;
    }

    const didSave = await Promise.resolve(saveCurrentPrompt());
    if (didSave) {
      showSavedFeedback();
      closeAnswerModal();
    }
    setIsSaveAndClosePending(false);
  };

  const handleRecordSaveAndClose = useCallback(async () => {
    const didSave = await handleUploadAndSaveAudio();
    if (!didSave) {
      setIsSaveAndClosePending(false);
      return;
    }

    if (hasWriteChanges) {
      const didSaveWrite = await Promise.resolve(saveCurrentPrompt());
      if (!didSaveWrite) {
        setIsSaveAndClosePending(false);
        return;
      }
    }

    showSavedFeedback();
    setIsReplacingRecording(false);
    stopAnswerModalAudioPlayback();
    setIsSaveAndNextConfirmationStep(false);
    setCloseConfirmOpen(false);
    setIsAnswerModalOpen(false);
    setIsSaveAndClosePending(false);
  }, [handleUploadAndSaveAudio, hasWriteChanges, saveCurrentPrompt, showSavedFeedback, stopAnswerModalAudioPlayback]);

  const handleRecordSaveAndCloseRequest = () => {
    setIsSaveAndClosePending(true);
    if (isRecordingInProgress) {
      setPostStopAction('saveAndClose');
      stopRecording();
      return;
    }

    void handleRecordSaveAndClose();
  };

  const handleSaveAndNextStay = () => {
    setIsSaveAndNextConfirmationStep(false);
  };


  const closeAnswerModal = useCallback(() => {
    stopAnswerModalAudioPlayback();
    setIsSaveAndNextConfirmationStep(false);
    setCloseConfirmOpen(false);
    setIsAnswerModalOpen(false);
  }, [stopAnswerModalAudioPlayback]);

  const hasUnsavedCloseChanges = hasWriteChanges || hasRecordChanges || isRecordingInProgress;

  const requestCloseAnswerModal = useCallback(() => {
    if (isSaveAndNextConfirmationStep) {
      closeAnswerModal();
      return;
    }

    if (hasUnsavedCloseChanges) {
      setCloseConfirmOpen(true);
      return;
    }

    closeAnswerModal();
  }, [closeAnswerModal, hasUnsavedCloseChanges, isSaveAndNextConfirmationStep]);

  const handleAnswerModalBack = useCallback(() => {
    if (!isReplacingRecording) {
      requestCloseAnswerModal();
      return;
    }

    if (hasRecordChanges || isRecordingInProgress) {
      setCloseConfirmOpen(true);
      return;
    }

    setIsReplacingRecording(false);
  }, [hasRecordChanges, isRecordingInProgress, isReplacingRecording, requestCloseAnswerModal]);

  const handleCloseWithoutSaving = () => {
    setCloseConfirmOpen(false);
    if (isRecordingInProgress) {
      discardPendingOnStopRef.current = true;
      stopRecording();
    }
    closeAnswerModal();
  };

  const handleSaveAndClose = () => {
    if (hasRecordChanges || isRecordingInProgress) {
      handleRecordSaveAndCloseRequest();
      return;
    }

    if (hasWriteChanges) {
      void handleWriteSaveAndClose();
      return;
    }

    closeAnswerModal();
  };

  const clearCurrentPromptDraft = useCallback(() => {
    if (!currentPromptValue) {
      return;
    }

    setCurrentPromptValue('');
  }, [currentPromptValue, setCurrentPromptValue]);

  const moveToNextIncompletePrompt = useCallback(() => {
    const allPrompts = sections.flatMap((section) => section.prompts || []);
    const currentPromptId = selectedPrompt?.id;
    if (!allPrompts.length || !currentPromptId) {
      setCompleteSectionModalOpen(true);
      return;
    }

    const currentPromptFlatIndex = allPrompts.findIndex((prompt) => prompt.id === currentPromptId);
    if (currentPromptFlatIndex === -1) {
      setCompleteSectionModalOpen(true);
      return;
    }

    const nextIncompletePrompt = allPrompts.slice(currentPromptFlatIndex + 1).find((prompt) => !persistedCompletedByPromptId[prompt.id]);

    if (nextIncompletePrompt) {
      goToPrompt(nextIncompletePrompt.id);
      return;
    }

    setCompleteSectionModalOpen(true);
  }, [goToPrompt, persistedCompletedByPromptId, sections, selectedPrompt]);

  const handleSaveAndNextGo = () => {
    setIsSaveAndNextConfirmationStep(false);
    if (saveAndNextConfirmTimeoutRef.current) {
      window.clearTimeout(saveAndNextConfirmTimeoutRef.current);
    }

    saveAndNextConfirmTimeoutRef.current = window.setTimeout(() => {
      moveToNextIncompletePrompt();

      const answerModalBody = document.querySelector('.life-review-session-answer-modal .ant-modal-body');
      if (answerModalBody instanceof HTMLElement) {
        answerModalBody.scrollTo({ top: 0, behavior: 'smooth' });
      }
      saveAndNextConfirmTimeoutRef.current = null;
    }, 240);
  };

  const handleSaveAndNextModalKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusOrder = [saveAndNextPrimaryButtonRef.current, saveAndNextSecondaryButtonRef.current].filter(
      (element): element is HTMLButtonElement => Boolean(element)
    );

    if (!focusOrder.length) {
      return;
    }

    const currentIndex = focusOrder.findIndex((element) => element === document.activeElement);
    const fallbackIndex = event.shiftKey ? focusOrder.length - 1 : 0;
    const nextIndex = currentIndex === -1 ? fallbackIndex : (currentIndex + (event.shiftKey ? -1 : 1) + focusOrder.length) % focusOrder.length;

    focusOrder[nextIndex].focus();
    event.preventDefault();
  };

  const proceedSkipForNow = useCallback(() => {
    moveToNextIncompletePrompt();
  }, [moveToNextIncompletePrompt]);

  useEffect(() => {
    if (postStopAction === 'none') {
      return;
    }

    if (postStopAction === 'skip') {
      if (isRecordingInProgress) {
        return;
      }

      setPostStopAction('none');
      proceedSkipForNow();
      return;
    }

    if (!hasStoppedRecordingForSelectedPrompt) {
      return;
    }

    const action = postStopAction;
    setPostStopAction('none');

    if (action === 'saveAndNext') {
      void handleRecordSaveAndNext();
      return;
    }

    if (action === 'saveAndClose') {
      void handleRecordSaveAndClose();
    }
  }, [
    handleRecordSaveAndClose,
    handleRecordSaveAndNext,
    hasStoppedRecordingForSelectedPrompt,
    isRecordingInProgress,
    postStopAction,
    proceedSkipForNow,
    recorderStatus
  ]);

  const handleSkipForNow = (hasUnsavedChanges: boolean) => {
    if (hasUnsavedChanges) {
      setSkipConfirmOpen(true);
      return;
    }

    proceedSkipForNow();
  };

  const confirmSkipForNow = () => {
    setSkipConfirmOpen(false);
    clearCurrentPromptDraft();

    if (isRecordingInProgress) {
      setPostStopAction('skip');
      discardPendingOnStopRef.current = true;
      stopRecording();
      return;
    }

    if (hasRecordChanges) {
      clearLocalRecording();
    }

    proceedSkipForNow();
  };

  function formatRecordingTime(seconds: number) {
    const finiteSeconds = Number.isFinite(seconds) ? seconds : 0;
    const safeSeconds = Math.max(0, Math.floor(finiteSeconds));
    const minutes = Math.floor(safeSeconds / 60)
      .toString()
      .padStart(2, '0');
    const remainingSeconds = (safeSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${remainingSeconds}`;
  }


  const loadSavedPlaybackUrl = useCallback(async () => {
    if (!selectedPromptResponseId) {
      setPlaybackUrl('');
      setPlaybackStatus('idle');
      return;
    }

    try {
      setPlaybackStatus('loading');
      const { audioDownloadUrl } = await getAudioDownloadUrl({ responseId: selectedPromptResponseId }).unwrap();
      setPlaybackUrl(audioDownloadUrl);
      setPlaybackStatus('ready');
    } catch (error: any) {
      setPlaybackStatus('error');
      setMediaError(error?.data?.message || error?.message || 'Unable to load saved audio right now.');
    }
  }, [getAudioDownloadUrl, selectedPromptResponseId, setPlaybackStatus]);

  useEffect(() => {
    let isDisposed = false;

    const hydrateSavedPlayback = async () => {
      setPlaybackUrl('');

      if (!selectedPrompt?.id || hasLocalRecordingForSelectedPrompt) {
        setPlaybackStatus('idle');
        return;
      }

      if (!hasSavedAudioForSelectedPrompt) {
        setPlaybackStatus('idle');
        return;
      }

      if (selectedPromptHydratedAudioUrl) {
        if (!isDisposed) {
          setPlaybackUrl(selectedPromptHydratedAudioUrl);
          setPlaybackStatus('ready');
        }
        return;
      }

      if (!selectedPromptResponseId) {
        setPlaybackStatus('idle');
        return;
      }

      try {
        setPlaybackStatus('loading');
        const { audioDownloadUrl } = await getAudioDownloadUrl({ responseId: selectedPromptResponseId }).unwrap();
        if (!isDisposed) {
          setPlaybackUrl(audioDownloadUrl);
          setPlaybackStatus('ready');
        }
      } catch (error: any) {
        if (!isDisposed) {
          setPlaybackStatus('error');
          setMediaError(error?.data?.message || error?.message || 'Unable to load saved audio right now.');
        }
      }
    };

    void hydrateSavedPlayback();

    return () => {
      isDisposed = true;
    };
  }, [
    getAudioDownloadUrl,
    hasLocalRecordingForSelectedPrompt,
    hasSavedAudioForSelectedPrompt,
    selectedPrompt?.id,
    selectedPromptHydratedAudioUrl,
    selectedPromptPersistedResponse?.hasAudio,
    selectedPromptPersistedResponse?.audioStorageKey,
    selectedPromptResponseId,
    setPlaybackStatus
  ]);

  useEffect(() => {
    if (!hasTranscriptTab && activeTab === 'transcript') {
      setActiveTab('record');
    }
  }, [activeTab, hasTranscriptTab]);

  useEffect(() => {
    if (recorderStatus === 'recording') {
      if (!recordingStartedAtRef.current) {
        recordingStartedAtRef.current = Date.now() - recordingElapsedSeconds * 1000;
      }

      const intervalId = window.setInterval(() => {
        const base = recordingStartedAtRef.current || Date.now();
        setRecordingElapsedSeconds(Math.floor((Date.now() - base) / 1000));
      }, 250);

      return () => window.clearInterval(intervalId);
    }

    if (recorderStatus === 'paused') {
      recordingStartedAtRef.current = null;
    }

    if (recorderStatus === 'idle') {
      recordingStartedAtRef.current = null;
      if (!hasLocalRecordingForSelectedPrompt) {
        setRecordingElapsedSeconds(0);
      }
    }

    return undefined;
  }, [hasLocalRecordingForSelectedPrompt, recorderStatus, recordingElapsedSeconds]);

  useEffect(() => {
    if (recorderStatus !== 'recording') {
      return;
    }

    evaluateGuardrails('duration');
  }, [evaluateGuardrails, recorderStatus, recordingElapsedSeconds]);

  useEffect(() => {
    const transcriptNode = transcriptPreviewTextRef.current;
    if (!transcriptNode || !selectedPromptTranscriptText) {
      setHasTranscriptPreviewOverflow(false);
      return;
    }

    let frameId: number | null = null;

    const checkOverflow = () => {
      const collapsedHeight = transcriptNode.clientHeight;
      const measuredWidth = transcriptNode.clientWidth;

      if (!collapsedHeight || !measuredWidth) {
        setHasTranscriptPreviewOverflow(false);
        return;
      }

      const clone = transcriptNode.cloneNode(true) as HTMLSpanElement;
      clone.classList.add('is-expanded');
      clone.style.position = 'absolute';
      clone.style.visibility = 'hidden';
      clone.style.pointerEvents = 'none';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      clone.style.width = `${measuredWidth}px`;

      transcriptNode.parentElement?.appendChild(clone);
      const expandedHeight = clone.scrollHeight;
      clone.remove();

      const hasOverflow = expandedHeight - collapsedHeight > 1;
      setHasTranscriptPreviewOverflow(hasOverflow);
    };

    const scheduleCheckOverflow = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        checkOverflow();
      });
    };

    scheduleCheckOverflow();
    const timeoutId = window.setTimeout(scheduleCheckOverflow, 150);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        scheduleCheckOverflow();
      });

      resizeObserver.observe(transcriptNode);

      const previewContainer = transcriptNode.parentElement;
      if (previewContainer) {
        resizeObserver.observe(previewContainer);
      }

      const modalBody = transcriptNode.closest('.ant-modal-body');
      if (modalBody instanceof HTMLElement) {
        resizeObserver.observe(modalBody);
      }
    }

    window.addEventListener('resize', scheduleCheckOverflow);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.clearTimeout(timeoutId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleCheckOverflow);
    };
  }, [activeTab, isAnswerModalOpen, isReplacingRecording, selectedPromptTranscriptText, showSavedRecordingCard]);
  const selectedSection = sections[selectedSectionIndex];
  const selectedSectionPrompts = selectedSection?.prompts || [];
  const selectedSectionAnsweredCount = selectedSectionPrompts.filter((prompt) => persistedCompletedByPromptId[prompt.id]).length;
  const selectedSectionProgressPercent = selectedSectionPrompts.length
    ? Math.round((selectedSectionAnsweredCount / selectedSectionPrompts.length) * 100)
    : 0;

  const getPromptStatus = useCallback(
    (promptId: string) => {
      const isPersistedCompleted = persistedCompletedByPromptId[promptId];
      const draftText = (draftByPromptId[promptId] || '').trim();
      const persistedResponse = persistedResponseByPromptId[promptId];
      const transcriptionStatus = (persistedResponse?.transcriptionStatus || '').toLowerCase();

      if (persistedResponse?.transcriptionError || transcriptionStatus === 'failed') {
        return 'failed';
      }

      if (['pending', 'processing', 'transcribing', 'queued', 'in_progress'].includes(transcriptionStatus)) {
        return 'transcribing';
      }

      if (isPersistedCompleted) {
        return 'answered';
      }

      if (draftText) {
        return 'draft';
      }

      return 'notStarted';
    },
    [draftByPromptId, persistedCompletedByPromptId, persistedResponseByPromptId]
  );

  const getStatusMeta = useCallback((status: 'answered' | 'draft' | 'notStarted' | 'transcribing' | 'failed') => {
    switch (status) {
      case 'answered':
        return { label: 'Answered', icon: <CheckOutlined /> };
      case 'draft':
        return { label: 'Draft', icon: <EditOutlined /> };
      case 'transcribing':
        return { label: 'Transcribing', icon: <LoadingOutlined /> };
      case 'failed':
        return { label: 'Failed', icon: <ExclamationCircleOutlined /> };
      default:
        return { label: 'Not started', icon: <ClockCircleOutlined /> };
    }
  }, []);

  return (
    <div className="life-review-session-page layout-full-height">
      <Header />

      <ContainerComponent className="layout-main-content">
        {isLoading ? (
          <div className="life-review-session-state life-review-session-loading">
            <Spin size="large" />
          </div>
        ) : !selectedPrompt ? (
          <div className="life-review-session-state">
            <Alert
              type="warning"
              showIcon
              message="No prompts available"
              description="We couldn't load guided prompts yet. Please try again shortly."
            />
          </div>
        ) : (
          <div className="life-review-session-state">
            <div className="life-review-session-list-header">
              <Link to="/app/life-story" className="life-review-session-list-back" aria-label="Back to Life Story hub">
                <LeftOutlined />
              </Link>
              <h1>{selectedSection?.title || selectedSection?.key || selectedSection?.sectionKey || 'Life Area'}</h1>
              <p>{selectedSection?.description || 'Reflect on this chapter of your life at your own pace.'}</p>
              <span className="life-review-session-list-progress">
                {selectedSectionAnsweredCount} of {selectedSectionPrompts.length} answered
              </span>
              <div className="life-review-session-list-progress-track" aria-hidden="true">
                <span className="life-review-session-list-progress-fill" style={{ width: `${selectedSectionProgressPercent}%` }} />
              </div>
            </div>

            <aside className="life-review-session-question-list">
              {selectedSectionPrompts.map((prompt) => {
                const status = getPromptStatus(prompt.id) as 'answered' | 'draft' | 'notStarted' | 'transcribing' | 'failed';
                const statusMeta = getStatusMeta(status);
                const persistedResponse = persistedResponseByPromptId[prompt.id];
                const preview = (
                  persistedResponse?.editedTranscriptText ||
                  persistedResponse?.transcriptText ||
                  persistedResponse?.responseText ||
                  persistedResponse?.displayText ||
                  ''
                ).trim();
                const isSelected = prompt.id === selectedPromptId;

                return (
                  <button
                    key={prompt.id}
                    type="button"
                    className={`life-review-session-question-card ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => {
                      goToPrompt(prompt.id);
                      setIsAnswerModalOpen(true);
                    }}
                  >
                    <span className={`life-review-session-question-status-icon status-${status}`}>{statusMeta.icon}</span>
                    <span className="life-review-session-question-content">
                      <strong>{getPromptDisplayText(prompt)}</strong>
                      <span className={`life-review-session-question-status status-${status}`}>{statusMeta.label}</span>
                      {preview && <span className="life-review-session-question-preview">{preview}</span>}
                    </span>
                    <span className="life-review-session-question-arrow">
                      <RightOutlined />
                    </span>
                  </button>
                );
              })}
            </aside>

            <Modal
              open={isAnswerModalOpen}
              onCancel={requestCloseAnswerModal}
              footer={null}
              closable={false}
              centered
              width={980}
              destroyOnHidden={false}
              className="life-review-session-answer-modal"
              title={null}
            >
              <section
                className={`life-review-session-card life-review-session-answer-panel ${isSaveAndNextPending ? 'is-saving-next' : ''} ${
                  activeTab === 'write' ? 'is-write-tab' : ''
                }`}
              >
                {isSaveAndNextConfirmationStep ? (
                  <div className="life-review-save-next-modal-body" role="dialog" aria-modal="true" aria-labelledby="life-review-save-next-title" onKeyDown={handleSaveAndNextModalKeyDown}>
                    <div className="life-review-save-next-icon-wrap" aria-hidden="true">
                      <span className="life-review-save-next-dot dot-one" />
                      <span className="life-review-save-next-dot dot-two" />
                      <span className="life-review-save-next-dot dot-three" />
                      <span className="life-review-save-next-dot dot-four" />
                      <span className="life-review-save-next-dot dot-five" />
                      <div className="life-review-save-next-check-circle">
                        <CheckOutlined />
                      </div>
                    </div>

                    <h3 id="life-review-save-next-title">Answer saved!</h3>
                    <p>Would you like to go to the next question?</p>

                    <button ref={saveAndNextPrimaryButtonRef} type="button" className="life-review-save-next-primary" onClick={() => handleSaveAndNextGo()}>
                      <span>Yes, next question</span>
                      <RightOutlined />
                    </button>
                    <button ref={saveAndNextSecondaryButtonRef} type="button" className="life-review-save-next-secondary" onClick={() => handleSaveAndNextStay()}>
                      Stay on this question
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="life-review-session-answer-header">
                      <button
                        type="button"
                        className="life-review-session-list-back"
                        aria-label={isReplacingRecording ? 'Back to saved recording' : 'Close answer modal'}
                        onClick={handleAnswerModalBack}
                      >
                        <LeftOutlined />
                      </button>
                      {saveStatusLabel ? <span className="life-review-session-autosaved">✓ {saveStatusLabel}</span> : null}
                    </div>

                    <p className="life-review-session-prompt">{getPromptDisplayText(selectedPrompt)}</p>
                    <p className="life-review-session-helper">
                      {hasLifeReviewPaidAccess
                        ? 'Reflect in your own words. You can record, write, or edit your transcript at any time.'
                        : 'Browse the questions in your Life Review. Upgrade to Premium or Unlimited to answer and share your story.'}
                    </p>

                    {!hasLifeReviewPaidAccess ? (
                      <Alert
                        type="info"
                        showIcon
                        message="Upgrade to answer and share your Life Review"
                        description="Answering questions and sharing your Life Review are available on Premium and Unlimited plans."
                        action={
                          <Button type="primary" onClick={() => navigate('/pricing')}>
                            Upgrade Plan
                          </Button>
                        }
                      />
                    ) : (
                      <>
                        <div className="life-review-session-tabs" role="tablist" aria-label="Answer input mode tabs">
                          <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'record'}
                            className={`life-review-session-tab ${activeTab === 'record' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('record')}
                          >
                            Record
                          </button>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'write'}
                            className={`life-review-session-tab ${activeTab === 'write' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('write')}
                          >
                            Write
                          </button>
                          {hasTranscriptTab && (
                            <button
                              type="button"
                              role="tab"
                              aria-selected={activeTab === 'transcript'}
                              className={`life-review-session-tab ${activeTab === 'transcript' ? 'is-active' : ''}`}
                              onClick={() => setActiveTab('transcript')}
                            >
                              Transcript
                            </button>
                          )}
                        </div>

                        {activeTab === 'record' && (
                  <div className={`life-review-record-panel ${recorderStatus === 'recording' ? 'is-recording' : ''}`}>
                    {showSavedRecordingCard ? (
                      <div className="life-review-saved-audio-card">
                        <div className="life-review-saved-audio-header">
                          <span className="life-review-saved-audio-status">
                            <span className="life-review-saved-audio-status-dot" aria-hidden="true" />
                            Recording saved
                          </span>

                        </div>

                        <div className="life-review-saved-audio-visual" role="presentation">
                          <div className="life-review-saved-audio-waveform">
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                          </div>
                        </div>

                        <div className="life-review-saved-audio-playback-controls">
                          <audio
                            controls
                            className="life-review-saved-audio-element"
                            src={playbackUrl}
                            onError={() => {
                              if (selectedPromptResponseId) {
                                void loadSavedPlaybackUrl();
                              }
                            }}
                          >
                            <span>Audio playback is not supported in your browser.</span>
                          </audio>
                        </div>

                        {selectedPromptTranscriptText && (
                          <div className="life-review-saved-audio-transcript-preview">
                            <p>Transcript preview</p>
                            <span ref={transcriptPreviewTextRef} className={isTranscriptPreviewExpanded ? 'is-expanded' : ''}>{selectedPromptTranscriptText}</span>
                            {(hasTranscriptPreviewOverflow || isTranscriptPreviewExpanded) && (
                              <button
                                type="button"
                                className="life-review-saved-audio-transcript-toggle"
                                onClick={() => setIsTranscriptPreviewExpanded((prev) => !prev)}
                              >
                                {isTranscriptPreviewExpanded ? 'see less' : '...see more'}
                              </button>
                            )}
                          </div>
                        )}

                        <div className="life-review-saved-audio-actions">
                          <button
                            type="button"
                            className={`life-review-saved-audio-action life-review-saved-audio-replace ${isReplacingRecording ? 'is-warning' : ''}`}
                            onClick={() => setIsReplacingRecording(true)}
                            title="Replace recording"
                          >
                            <span className="life-review-saved-audio-action-icon" aria-hidden="true">
                              <LuRotateCcw />
                            </span>
                            <span className="life-review-saved-audio-action-label">Replace Recording</span>
                          </button>
                          {hasTranscriptTab && (
                            <button
                              type="button"
                              className="life-review-saved-audio-action life-review-saved-audio-edit"
                              onClick={() => setActiveTab('transcript')}
                              title="Edit transcript"
                            >
                              <span className="life-review-saved-audio-action-icon" aria-hidden="true">
                                <LuPencilLine />
                              </span>
                              <span className="life-review-saved-audio-action-label">Edit Transcript</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        {hasSavedAudioForSelectedPrompt && isReplacingRecording && (
                          <div className="life-review-record-replace-warning">
                            <p>Recording a new answer will replace the current recording and transcript edits.</p>
                            <button
                              type="button"
                              className="life-review-record-replace-cancel"
                              onClick={() => setIsReplacingRecording(false)}
                            >
                              Back to current recording
                            </button>
                          </div>
                        )}

                        {!hasStoppedRecordingForSelectedPrompt && (
                          <>
                            <p className="life-review-record-instruction">{isRecordingActive ? 'Recording...' : isRecordingPaused ? 'Recording paused' : 'Tap the microphone to start recording'}</p>
                            <button
                              type="button"
                              className={`life-review-record-mic ${isRecordingActive ? 'is-active' : ''}`}
                              onClick={() => (isRecordingActive ? pauseRecording() : isRecordingPaused ? resumeRecording() : handleStartRecording())}
                              disabled={recorderStatus === 'acquiring_media'}
                              aria-label={isRecordingActive ? 'Pause recording' : isRecordingPaused ? 'Resume recording' : 'Start recording'}
                            >
                              {isRecordingActive ? <PauseOutlined /> : isRecordingPaused ? <CaretRightOutlined /> : <AudioOutlined />}
                            </button>
                          </>
                        )}

                        <p className="life-review-record-timer">{formatRecordingTime(recordingElapsedSeconds)}</p>
                        <p className="life-review-record-subtext">Recording will appear here</p>
                      </>
                    )}

                    {hasLocalRecordingForSelectedPrompt && (
                      <div className="life-review-record-captured">
                        <p className="life-review-record-ready">Recording ready</p>
                        <audio className="life-review-session-audio-player" controls src={mediaBlobUrl}>
                          <track kind="captions" />
                        </audio>
                      </div>
                    )}

                    {!showSavedRecordingCard && (
                      <div className={`life-review-record-controls ${hasStoppedRecordingForSelectedPrompt ? 'is-stopped' : isRecordingInProgress ? 'is-active' : 'is-idle'}`}>
                        {hasStoppedRecordingForSelectedPrompt ? (
                          <>
                            <button
                              type="button"
                              className="life-review-record-control-button is-secondary"
                              onClick={() => handleStartOverRecording()}
                              aria-label="Start over"
                              title="Start over"
                            >
                              <LuRotateCcw /> <span className="life-review-record-control-label">Start Over</span>
                            </button>
                            <button
                              type="button"
                              className="life-review-record-control-button is-discard"
                              onClick={() => handleDiscardRecording()}
                              aria-label="Discard recording"
                              title="Discard"
                            >
                              <DeleteOutlined /> <span className="life-review-record-control-label">Discard</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className={`life-review-record-control-button is-primary ${isRecordingActive ? 'is-pause' : isRecordingPaused ? 'is-resume' : 'is-record'}`}
                              onClick={() => {
                                if (isRecordingActive) {
                                  pauseRecording();
                                  return;
                                }

                                if (isRecordingPaused) {
                                  resumeRecording();
                                  return;
                                }

                                void handleStartRecording();
                              }}
                              disabled={recorderStatus === 'acquiring_media'}
                              aria-label={isRecordingActive ? 'Pause recording' : isRecordingPaused ? 'Resume recording' : 'Record'}
                              title={isRecordingActive ? 'Pause' : 'Record'}
                            >
                              {isRecordingActive ? <PauseOutlined /> : isRecordingPaused ? <CaretRightOutlined /> : <span className="life-review-record-dot" aria-hidden="true" />}
                              <span className="life-review-record-control-label">{isRecordingActive ? 'Pause' : isRecordingPaused ? 'Resume' : 'Record'}</span>
                            </button>
                            <button
                              type="button"
                              className="life-review-record-control-button is-secondary"
                              onClick={() => stopRecording()}
                              disabled={!isRecordingInProgress}
                              aria-label="Stop recording"
                              title="Stop"
                            >
                              <span className="life-review-stop-icon" aria-hidden="true" /> <span className="life-review-record-control-label">Stop</span>
                            </button>
                            <button
                              type="button"
                              className="life-review-record-control-button is-discard"
                              onClick={() => handleDiscardRecording()}
                              disabled={!isRecordingInProgress && !hasLocalRecordingForSelectedPrompt}
                              aria-label="Discard recording"
                              title="Discard"
                            >
                              <DeleteOutlined /> <span className="life-review-record-control-label">Discard</span>
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    <div className="life-review-session-bottom-actions life-review-record-modal-actions">
                      <Button
                        type="primary"
                        className="life-review-answer-action-next"
                        onClick={handleRecordSaveAndNextRequest}
                        disabled={!canSubmitRecordSave || uploadStatus === 'preparing' || uploadStatus === 'uploading' || isSaveAndNextPending}
                        loading={uploadStatus === 'preparing' || uploadStatus === 'uploading' || isSaveAndNextPending}
                      >
                        <span>{isSaveAndNextPending ? 'Saving...' : 'Save & Next'}</span>
                      </Button>
                      <button
                        type="button"
                        className="life-review-write-skip"
                        onClick={() => handleSkipForNow(hasRecordChanges || isRecordingInProgress)}
                        disabled={uploadStatus === 'preparing' || uploadStatus === 'uploading'}
                      >
                        Skip for now
                      </button>
                    </div>
                    {playbackUrl && !showSavedRecordingCard && !isReplacingRecording && (
                      <audio
                        className="life-review-session-audio-player"
                        controls
                        src={playbackUrl}
                        onError={() => {
                          if (selectedPromptResponseId) {
                            void loadSavedPlaybackUrl();
                          }
                        }}
                      >
                        <track kind="captions" />
                      </audio>
                    )}
                    {mediaError && <Alert type="error" showIcon message={mediaError} />}
                    {recorderError && <Alert type="error" showIcon message={recorderError} />}
                  </div>
                        )}

                        {activeTab === 'write' && (
                      <div className="life-review-write-panel">
                        <div className="life-review-write-panel-scroll">
                          <label className="life-review-write-label" htmlFor="life-review-write-answer">
                            Your written answer
                          </label>
                          <Input.TextArea
                            id="life-review-write-answer"
                            value={currentPromptValue}
                            onChange={(event) => setCurrentPromptValue(event.target.value)}
                            className="life-review-write-textarea"
                            placeholder="Write your response here"
                            autoSize={{ minRows: 8 }}
                          />
                          <p className="life-review-write-counter">{currentPromptValue.length} characters</p>
                        </div>

                        <div className="life-review-session-bottom-actions life-review-write-actions">
                          <Button
                            type="primary"
                            className="life-review-answer-action-next"
                            onClick={() => handleWriteSaveAndNext()}
                            disabled={(!hasWriteChanges && !hasRecordChanges && !isRecordingInProgress) || isSaving || isUploadingRecording || isSaveAndNextPending}
                            loading={isSaving || isUploadingRecording || isSaveAndNextPending}
                          >
                            <span>{isSaveAndNextPending ? 'Saving...' : 'Save & Next'}</span>
                          </Button>
                          <button
                            type="button"
                            className="life-review-write-skip"
                            onClick={() => handleSkipForNow(hasWriteChanges || hasRecordChanges || isRecordingInProgress)}
                            disabled={isSaving || isUploadingRecording}
                          >
                            Skip for now
                          </button>
                        </div>
                        {saveError && <Alert type="error" showIcon message={saveError} />}
                      </div>
                        )}

                        {activeTab === 'transcript' && hasTranscriptTab && (
                  <div className="life-review-transcript-panel">
                    <p className="life-review-transcript-title">Audio Transcript</p>
                    <div className="life-review-transcript-raw">{selectedPromptTranscriptText}</div>
                    <label className="life-review-write-label" htmlFor="life-review-transcript-edit">
                      Edit your transcript
                    </label>
                    <Input.TextArea
                      id="life-review-transcript-edit"
                      value={transcriptDraft}
                      onChange={(event) => setTranscriptDraft(event.target.value)}
                      className="life-review-write-textarea"
                      autoSize={{ minRows: 6 }}
                      disabled={!selectedPromptTranscriptEditorInitialValue.trim()}
                    />
                    <div className="life-review-write-actions">
                      <Button
                        type="primary"
                        className="life-review-answer-action-next"
                        onClick={() => handleSaveTranscriptEdit()}
                        loading={isSavingTranscriptEdit}
                        disabled={
                          !selectedPromptTranscriptEditorInitialValue.trim() ||
                          !transcriptDraft.trim() ||
                          transcriptDraft === selectedPromptTranscriptEditorInitialValue
                        }
                      >
                        Save Transcript
                      </Button>
                      {transcriptStatus === 'failed' && (
                        <Button type="default" onClick={() => handleRetryTranscription()} loading={isRetryingTranscript} disabled={!selectedPromptResponseId}>
                          Retry transcription
                        </Button>
                      )}
                    </div>
                    {transcriptStatus === 'pending' && hasSavedAudioForSelectedPrompt && <p className="life-review-session-help-text">Transcribing...</p>}
                    {transcriptSaveSuccess && <p className="life-review-transcript-success">{transcriptSaveSuccess}</p>}
                    {transcriptError && <div className="life-review-transcript-error">{transcriptError}</div>}
                  </div>
                        )}
                      </>
                    )}
                  </>
                )}

              </section>
            </Modal>

            <Modal
              open={skipConfirmOpen}
              onCancel={() => setSkipConfirmOpen(false)}
              footer={null}
              centered
              width={420}
              className="life-review-skip-confirm-modal"
              title={null}
            >
              <div className="life-review-skip-confirm-body">
                <h3>Skip this question for now?</h3>
                <p>
                  If you skip now, your unsaved changes for this question will be lost.
                  <br />
                  You can return later, but this draft will not be kept.
                </p>
                <div className="life-review-skip-confirm-actions">
                  <Button type="primary" onClick={() => confirmSkipForNow()}>
                    Skip Question
                  </Button>
                  <Button type="default" onClick={() => setSkipConfirmOpen(false)}>
                    Keep Editing
                  </Button>
                </div>
              </div>
            </Modal>

            <Modal
              open={closeConfirmOpen}
              onCancel={() => setCloseConfirmOpen(false)}
              footer={null}
              centered
              width={520}
              className="life-review-session-complete-modal"
              title={null}
              destroyOnHidden={false}
            >
              <div className="life-review-session-complete-card life-review-close-confirm-card">
                <h3>Unsaved changes</h3>
                <p>If you close now without saving, your progress for this question will be lost.</p>
                <div className="life-review-close-confirm-actions">
                  <Button type="primary" onClick={handleSaveAndClose} loading={isSaveAndClosePending} disabled={isSaveAndClosePending}>
                    Save & Close
                  </Button>
                  <Button type="default" onClick={handleCloseWithoutSaving} disabled={isSaveAndClosePending}>
                    Close
                  </Button>
                </div>
              </div>
            </Modal>

            <Modal
              open={completeSectionModalOpen}
              onCancel={() => setCompleteSectionModalOpen(false)}
              footer={null}
              centered
              width={420}
              className="life-review-skip-confirm-modal"
              title={null}
            >
              <div className="life-review-skip-confirm-body">
                <h3>You’ve completed this section.</h3>
                <div className="life-review-skip-confirm-actions">
                  <Button
                    type="primary"
                    onClick={() => {
                      setIsAnswerModalOpen(false);
                      setCompleteSectionModalOpen(false);
                    }}
                  >
                    Return to Life Areas
                  </Button>
                </div>
              </div>
            </Modal>

            {saveToastMessage && <div className="life-review-save-toast">{saveToastMessage}</div>}
          </div>
        )}
      </ContainerComponent>

      <Footer />
    </div>
  );
}
